<template>
  <v-container id="evro_import">
    <v-dialog v-model="dialog" persistent max-width="500px">
      <v-card>
        <v-card-title>{{ $t('credentials.evrotrust.title') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    id="credential_name_field"
                    v-model="credential.description"
                    :label="$t('credentials.evrotrust.description')"
                    required
                    :rules="[v => !!v || $t('credentials.evrotrust.descriptionRequired')]"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    id="email_field"
                    v-model="credential.evroEmail"
                    :label="$t('credentials.evrotrust.evroEmail')"
                    required
                    :rules="[
                      v => !!v || $t('credentials.evrotrust.emailRequired'),
                      v => /.+@.+\..+/.test(v) || $t('credentials.evrotrust.emailValid')
                    ]"
                    type="email"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-expansion-panels v-model="advancedPanel" flat>
                    <v-expansion-panel>
                      <v-expansion-panel-header class="px-0">
                        <div class="d-flex align-center">
                          <v-icon left>mdi-cog</v-icon>
                          {{ $t('credentials.evrotrust.advancedOptions') }}
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="px-0">
                        <v-row>
                          <v-col cols="12">
                            <v-select
                              v-model="credential.type"
                              :items="typeOptions"
                              item-text="text"
                              item-value="value"
                              :label="$t('credentials.evrotrust.type')"
                              required
                              :rules="[v => !!v || $t('credentials.evrotrust.typeRequired')]"
                              @change="handleTypeChange"
                            ></v-select>
                          </v-col>

                          <v-col cols="12">
                            <v-select
                              v-model="credential.coverage"
                              :items="availableCoverageOptions"
                              :label="$t('credentials.evrotrust.coverage')"
                              required
                              :rules="[v => v !== null && v !== undefined || $t('credentials.evrotrust.coverageRequired')]"
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn id="cancel_button" class="v-btn-back" @click="cancel()">{{ $t('main.cancel') }}</v-btn>
          <v-btn
            id="import_button"
            color="primary"
            :disabled="!valid"
            @click="importCredential"
          >
            {{ $t('importCredential.import') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mobile App Interaction Overlay -->
    <v-dialog v-model="showMobileOverlay" persistent max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('credentials.evrotrust.actionRequired') }}</v-card-title>
        <v-card-text class="text-center py-4">
          <v-icon size="64" color="primary" class="mb-4">mdi-cellphone</v-icon>
          <p>{{ $t('credentials.evrotrust.mobileActivationMessage') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import eventBus, { showToastSuccess, showToastError } from '../../event-bus'
import evro from '../../services/api/evro'
import CommonError from '../CommonError'

export default {
  name: 'ImportEvro',

  data() {
    return {
      dialog: true,
      valid: false,
      loading: false,
      advancedPanel: null, // Controls expansion panel state (null = closed)
      // use config default timeout if not specified
      evroTrustAuthorizationTimeoutMilis: this.$store.state.remoteConfig?.evroAuthorizationTimeoutSecs ? this.$store.state.remoteConfig.evroAuthorizationTimeoutSecs * 1000 : undefined,
      showMobileOverlay: false,
      credential: {
        description: '',
        evroEmail: '',
        type: 1, // Default to qualified
        coverage: 20000 // Default coverage value
      },
      typeOptions: [
        { text: this.$t('credentials.evrotrust.qualified'), value: 1 },
        { text: this.$t('credentials.evrotrust.advanced'), value: 2 }
      ],
      coverageOptions: {
        qualified: [0, 2000, 20000, 100000, 250000],
        advanced: [500]
      }
    }
  },

  computed: {
    availableCoverageOptions() {
      if (this.credential.type === 1) {
        return this.coverageOptions.qualified
      } else if (this.credential.type === 2) {
        return this.coverageOptions.advanced
      } else {
        return []
      }
    }
  },

  methods: {
    handleTypeChange() {
      // Set appropriate default coverage based on type
      if (this.credential.type === 1) {
        this.credential.coverage = 20000 // Default for qualified
      } else if (this.credential.type === 2) {
        this.credential.coverage = 500 // Default for advanced
      } else {
        this.credential.coverage = null
      }
    },

    cancel() {
      this.$router.go(-1)
    },

    async importCredential() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      try {
        const dto = {
          description: this.credential.description,
          evroEmail: this.credential.evroEmail,
          coverage: this.credential.coverage,
          type: this.credential.type
        }

        this.showMobileOverlay = true

        const credentialRef = await evro.createEvroRef(dto, this.evroTrustAuthorizationTimeoutMilis)
        this.showMobileOverlay = false
        eventBus.$emit('refreshCredentials')
        showToastSuccess(this.$t('importCredential.importSucces'))
        this.$router.go(-1)
      } catch (error) {
        this.showMobileOverlay = false
        this.loading = false
        if (error?.response?.data?.code === CommonError.SERVICE_UNAVAILABLE) {
          showToastError(this.$t('documentSignatureCustomization.content.signature.toastMessages.sicAbortedEvroImport'))
        } else {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
        // showToastError(this.$t('importCredential.importError'))
        // console.error('An error occured during the import.', error)
      }
    }

  }
}
</script>
